import React from 'react';
import { useUser } from '../context/UserContext';
import { Eye, Shield, Lock, ArrowRight, Loader } from 'lucide-react';

const Splash: React.FC = () => {
  const { login, user, loading, firebaseUser, logout } = useUser();

  const handleLogin = async () => {
    await login();
  };

  if (loading) {
      return (
          <div className="min-h-screen bg-paper flex items-center justify-center">
              <div className="flex flex-col items-center">
                  <div className="w-16 h-16 border-4 border-nunchi-200 border-t-nunchi-700 rounded-full animate-spin mb-4"></div>
                  <p className="text-nunchi-600 font-serif italic">Loading Nunchi Master...</p>
              </div>
          </div>
      )
  }

  // Pending Approval State
  if (firebaseUser && !user.isApproved) {
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center p-6 text-center animate-fadeIn">
         <div className="max-w-md w-full bg-white p-10 rounded-3xl shadow-xl border border-nunchi-200 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-amber-400 to-nunchi-600"></div>
            
            <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 text-amber-500">
                <Lock size={40} />
            </div>

            <h1 className="text-2xl font-serif font-bold text-nunchi-900 mb-2">Access Pending</h1>
            <p className="text-nunchi-600 mb-6 leading-relaxed">
                Welcome, <strong>{user.name}</strong>. <br/>
                Your account is currently under review. <br/>
                <span className="text-sm mt-2 block opacity-75">
                    Please contact the administrator (mkajanth@gmail.com) to approve your access.
                </span>
            </p>

            <div className="bg-nunchi-50 p-4 rounded-xl mb-8 flex items-center justify-center gap-2 text-nunchi-500 text-sm">
                 <Loader size={16} className="animate-spin" /> Waiting for approval...
            </div>

            <button 
                onClick={logout}
                className="text-nunchi-400 hover:text-nunchi-600 font-bold text-sm underline"
            >
                Sign Out
            </button>
         </div>
      </div>
    );
  }

  // Initial Login State
  return (
    <div className="min-h-screen bg-paper flex flex-col relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-[-10%] right-[-10%] w-[50vh] h-[50vh] bg-nunchi-200 rounded-full blur-[100px] opacity-30"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-[50vh] h-[50vh] bg-amber-100 rounded-full blur-[100px] opacity-30"></div>

      <div className="flex-1 flex flex-col items-center justify-center p-6 relative z-10">
        <div className="max-w-xl w-full text-center">
            
            <div className="mb-12 animate-slideDown">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-nunchi-800 text-white rounded-3xl mb-6 shadow-2xl rotate-3">
                    <Eye size={48} />
                </div>
                <h1 className="text-5xl md:text-7xl font-serif font-bold text-nunchi-900 mb-4 tracking-tight">
                    Nunchi<span className="text-nunchi-400">Master</span>
                </h1>
                <p className="text-xl text-nunchi-600 font-serif italic">
                    "The art of reading the room."
                </p>
            </div>

            <div className="space-y-6 animate-slideUp">
                <div className="bg-white/60 backdrop-blur-md p-6 rounded-2xl border border-nunchi-100 shadow-sm max-w-sm mx-auto">
                    <p className="text-nunchi-700 mb-6 leading-relaxed">
                        Master social intuition, find your flow, and discover your purpose through ancient Eastern philosophies.
                    </p>
                    
                    <button 
                        onClick={handleLogin}
                        className="w-full bg-nunchi-800 text-white py-4 rounded-xl font-bold text-lg hover:bg-nunchi-900 hover:scale-[1.02] transition-all shadow-lg flex items-center justify-center gap-3"
                    >
                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" alt="Google" />
                        Sign in with Google
                    </button>
                </div>

                <div className="flex justify-center gap-8 text-nunchi-400 text-xs font-bold uppercase tracking-widest">
                    <span className="flex items-center gap-1"><Shield size={12} /> Invite Only</span>
                    <span>•</span>
                    <span>AI Powered</span>
                    <span>•</span>
                    <span>Private</span>
                </div>
            </div>

        </div>
      </div>

      <footer className="w-full text-center py-6 text-nunchi-300 text-xs relative z-10">
          &copy; {new Date().getFullYear()} Nunchi Master. All rights reserved.
      </footer>
    </div>
  );
};

export default Splash;